import { submitLogout } from 'actions/auth';
import { useAppDispatch } from 'hooks/reduxHooks';
import useTeamsState from 'hooks/stateHooks/useTeamsState';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { getLocalStorage, setLocalStorage } from 'utils/localStorage';

type MismatchErrorHandler = {
  mismatchError: boolean;
  setMismatchError: Dispatch<SetStateAction<boolean | null>>;
  setMismatchFalse: () => void;
};

const getMismatchErrorStorage = () =>
  getLocalStorage<boolean, boolean>('auth-mismatch-error', false);

const setMismatchErrorStorage = (val: boolean) =>
  setLocalStorage('auth-mismatch-error', val);

/**
 * On 365 login - user is logged in to keycloak irrespective
 * of whether they have a beecastle account, or have
 * been invited to signup.
 *
 * So we must log the user out so they can use another account if desired
 * and display an error message.
 *
 * On logout server forces hard refresh so FE loses error state
 * and can't notify the user.
 *
 * This hook forces logout on the keycloak mismatch condition
 * being met, stores error state in local storage, and cleans
 * up after first page load.
 * stores error state in local storage for use after page
 * load.
 *
 * ---
 *
 * @param mismatchCondition - state that determines whether
 *   365 login has occured, but no user is present or invited
 *   on beecastle.
 * @param logoutOnMismatch - logs the user out before displaying
 *  the error message.  This involves a server defined 302 redirect
 *  back to login page.  If logout unecessary set this to false,
 *  A page reload will still be used to take advantage of the same
 *  state management.
 * @param redirect - tells the server to force a 302 redirect.
 * @param reload - if not logging out - reload will reload page,
 *  displaying error immediately.
 * @returns - obj
 * @property - obj.mismatchError - whether error message should
 *  be displayed
 * @property - obj.setMismatchError - func to change the current
 *  error state (e.g. on click of a login button)
 * @property - obj.setMismatchFalse - for convienience.
 */
const useMismatchError = (
  mismatchCondition: boolean,
  logoutOnMismatch = true,
  redirect = true,
  reload = true,
): MismatchErrorHandler => {
  const dispatch = useAppDispatch();
  const { inTeams } = useTeamsState();
  const logoutRef = useRef(false);

  const mismatchErrorInit = getMismatchErrorStorage();
  const [mismatchError, setMismatchError] = useState<boolean | null>(
    mismatchErrorInit || null,
  );

  const setMismatchFalse = () => setMismatchError(false);

  useEffect(() => {
    if (getMismatchErrorStorage()) {
      setMismatchError(true);
      /*
        Setimeout gaurds against multiple quick remounts that
        seem to happen in some cases.
      */
      setTimeout(() => setMismatchErrorStorage(false), 500);
    }
  }, []);

  useEffect(() => {
    const doMismatchCheck = async () => {
      if (mismatchCondition && !logoutRef.current && !logoutOnMismatch) {
        logoutRef.current = true;
        setMismatchErrorStorage(true);
        // reload to display error immediately.
        if (reload) {
          setTimeout(() => window.location.reload(), 100);
        }
      }
      if (mismatchCondition && !logoutRef.current && logoutOnMismatch) {
        // prevents multiple logout calls and page reloads.
        logoutRef.current = true;
        // sets the mismatch error to true.
        setMismatchErrorStorage(true);
        // must logout otherwise user can't try again with
        // different email.
        const logoutParams = inTeams
          ? {
              url: `/oauthteams?next=${window.location.pathname}`,
              keycloak: true,
              redirect: true,
            }
          : { url: null, keycloak: false, redirect: true };
        if (!redirect) {
          logoutParams.redirect = false;
        }
        await dispatch(submitLogout(logoutParams));
        // reload to display error immediately.
        if (reload) {
          setTimeout(() => window.location.reload(), 100);
        }
      }
    };
    doMismatchCheck();
  }, [
    dispatch,
    inTeams,
    mismatchCondition,
    logoutOnMismatch,
    redirect,
    reload,
  ]);

  return {
    mismatchError: !!mismatchError,
    setMismatchError,
    setMismatchFalse,
  };
};

export default useMismatchError;
